import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, Package, Truck, Scan } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PickExecution() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [barcode, setBarcode] = useState('');
  const [currentStep, setCurrentStep] = useState('picking'); // picking, packing, shipping
  const barcodeInputRef = useRef(null);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  useEffect(() => {
    // Auto-focus on barcode input
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, [currentStep]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);

      // Determine current step based on order status
      if (response.data.status === 'picked') {
        setCurrentStep('packing');
      } else if (response.data.status === 'packed') {
        setCurrentStep('shipping');
      }
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Failed to load order details');
      navigate('/pick-list');
    } finally {
      setLoading(false);
    }
  };

  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();
    if (!barcode.trim()) return;

    try {
      // Find product by barcode
      const response = await axios.get(`${API}/products/barcode/${barcode}`);
      const product = response.data;

      // Check if product is in this order
      const orderItem = order.items.find((item) => item.product_id === product.id);
      if (!orderItem) {
        toast.error('This product is not in the current order');
        setBarcode('');
        return;
      }

      // Check if already fully picked
      if (orderItem.picked >= orderItem.quantity) {
        toast.warning('This item is already fully picked');
        setBarcode('');
        return;
      }

      // Confirm pick
      await axios.post(`${API}/orders/${orderId}/pick`, {
        product_id: product.id,
        quantity: 1,
      });

      toast.success(`Picked: ${product.name}`);
      setBarcode('');
      fetchOrder(); // Refresh order
    } catch (error) {
      console.error('Scan error:', error);
      if (error.response?.status === 404) {
        toast.error('Product not found');
      } else {
        toast.error('Failed to confirm pick');
      }
      setBarcode('');
    }
  };

  const handlePack = async () => {
    try {
      await axios.post(`${API}/orders/${orderId}/pack`);
      toast.success('Order packed successfully!');
      setCurrentStep('shipping');
      fetchOrder();
    } catch (error) {
      console.error('Failed to pack order:', error);
      toast.error(error.response?.data?.detail || 'Failed to pack order');
    }
  };

  const handleShip = async () => {
    try {
      await axios.post(`${API}/orders/${orderId}/ship`, {
        tracking_number: null,
        notes: null,
      });
      toast.success('Order shipped successfully!');
      navigate('/pick-list');
    } catch (error) {
      console.error('Failed to ship order:', error);
      toast.error(error.response?.data?.detail || 'Failed to ship order');
    }
  };

  const calculateProgress = () => {
    if (!order) return 0;
    const totalItems = order.items.reduce((sum, item) => sum + item.quantity, 0);
    const pickedItems = order.items.reduce((sum, item) => sum + item.picked, 0);
    return (pickedItems / totalItems) * 100;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!order) return null;

  const progress = calculateProgress();
  const allPicked = progress === 100;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="pick-execution-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/pick-list')}
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              Order: {order.order_number}
            </h1>
            <p className="text-slate-600">Customer: {order.customer_name}</p>
          </div>
        </div>

        {/* Progress */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Progress</CardTitle>
              <Badge
                className={`${
                  currentStep === 'picking'
                    ? 'bg-blue-100 text-blue-800'
                    : currentStep === 'packing'
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-green-100 text-green-800'
                }`}
              >
                {currentStep === 'picking'
                  ? 'Picking'
                  : currentStep === 'packing'
                  ? 'Packing'
                  : 'Ready to Ship'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <Progress value={progress} className="h-3" />
            <p className="text-sm text-slate-600 mt-2">
              {order.items.reduce((sum, item) => sum + item.picked, 0)} of{' '}
              {order.items.reduce((sum, item) => sum + item.quantity, 0)} items picked
            </p>
          </CardContent>
        </Card>

        {/* Picking Phase */}
        {currentStep === 'picking' && (
          <>
            {/* Barcode Scanner */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scan className="h-5 w-5" />
                  Scan Product Barcode
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleBarcodeSubmit}>
                  <Input
                    ref={barcodeInputRef}
                    type="text"
                    placeholder="Scan or enter barcode..."
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    className="scan-input text-center"
                    autoFocus
                    data-testid="barcode-input"
                  />
                </form>
              </CardContent>
            </Card>

            {/* Items List */}
            <Card>
              <CardHeader>
                <CardTitle>Items to Pick</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {order.items.map((item, index) => {
                    const itemComplete = item.picked >= item.quantity;
                    return (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-4 border rounded-lg ${
                          itemComplete ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'
                        }`}
                        data-testid={`pick-item-${item.sku}`}
                      >
                        <div className="flex items-center gap-4">
                          {itemComplete ? (
                            <CheckCircle2 className="h-6 w-6 text-green-600" />
                          ) : (
                            <Package className="h-6 w-6 text-slate-400" />
                          )}
                          <div>
                            <div className="font-semibold text-slate-900">{item.name}</div>
                            <div className="text-sm text-slate-600">
                              SKU: <code className="font-mono">{item.sku}</code>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold font-mono text-slate-900">
                            {item.picked} / {item.quantity}
                          </div>
                          <div className="text-xs text-slate-600">Picked</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Complete Picking */}
            {allPicked && (
              <Button
                onClick={handlePack}
                className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
                data-testid="confirm-pack-button"
              >
                <CheckCircle2 className="h-5 w-5 mr-2" />
                Confirm Pack
              </Button>
            )}
          </>
        )}

        {/* Packing Phase */}
        {currentStep === 'packing' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-6 w-6" />
                Packing Complete
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-600">All items have been picked and packed.</p>
              <Button
                onClick={handleShip}
                className="w-full h-14 text-lg bg-[#1680e5] hover:bg-[#1680e5]/90"
                data-testid="ship-order-button"
              >
                <Truck className="h-5 w-5 mr-2" />
                Ship Order
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
